import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import teamsData from '../data/teams';
import MAJOR_CITIES from '../data/cities';
import * as kiwiApi from '../services/kiwiApi';
import { fetchTeamEvents } from '../services/teamEvents';
import { buildExpediaFlightUrl } from '../services/expediaAffiliate';
import CitySearch from './CitySearch';

// Helper function to format dates (if you don't have one)
const formatDate = (dateTimeString) => {
  if (!dateTimeString) return 'Date TBD';
  try {
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    });
  } catch (e) {
    return 'Date TBD';
  }
};

// Format an ISO timestamp into a short local time/day label, e.g. "Sat 3:15 PM".
const formatTripTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', hour: 'numeric', minute: '2-digit',
    });
  } catch (e) {
    return null;
  }
};


const TicketList = () => {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [processedEvents, setProcessedEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false); // For detail fetch step
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState('');
  const [flightPrices, setFlightPrices] = useState({});
  const [showFlightDetailsMap, setShowFlightDetailsMap] = useState({});

  // Track which event IDs we've already kicked off a flight fetch for, so the
  // flight-fetch effect doesn't need flightPrices in its deps (avoids re-render storm).
  const attemptedFlightFetchesRef = useRef(new Set());

  useEffect(() => {
    const currentTeam = teamsData.find(t => t.id === teamId);
    setTeam(currentTeam);
  }, [teamId]);

  // Function to check if flight is needed based on venue location
  const needsFlight = useCallback((userLoc, venue) => {
    if (!userLoc || !venue?.city?.name) return false;
    const matchedUserCity = MAJOR_CITIES.find(city => city.fullName === userLoc);
    const venueCity = venue.city.name;
    const venueState = venue.state?.stateCode;

    if (!matchedUserCity) return false; // Only calculate if user city is known

    // Basic check: if city names are different, assume flight needed
    // More robust check would involve comparing state/country or distance
    return matchedUserCity.name.toLowerCase() !== venueCity.toLowerCase() ||
           (venueState && matchedUserCity.stateCode !== venueState);
  }, []);

    // Fetch flight price for a single event using the event-aware Kiwi search
    const fetchFlightPrice = useCallback(async (eventId, fromCityFullName, eventDateTime, venue, sport) => {
      if (!eventDateTime || !venue?.city?.name || !fromCityFullName) return;

      const sourceCity = MAJOR_CITIES.find(city => city.fullName === fromCityFullName);
      const destinationCity = MAJOR_CITIES.find(city =>
        city.name.toLowerCase() === venue.city.name.toLowerCase() ||
        city.fullName.toLowerCase().includes(venue.city.name.toLowerCase()) ||
        (venue.state && city.stateCode === venue.state.stateCode && city.name.toLowerCase().includes(venue.city.name.toLowerCase()))
      );

      if (!sourceCity?.iata || !destinationCity?.iata || sourceCity.iata === destinationCity.iata) {
        return;
      }

      try {
        const flightData = await kiwiApi.searchEventFlights(
          sourceCity.iata,
          destinationCity.iata,
          eventDateTime,
          sport
        );

        if (flightData) {
          setFlightPrices(prev => ({
            ...prev,
            [eventId]: {
              price: flightData.price,
              airlines: flightData.airlines,
              duration: flightData.duration,
              deep_link: flightData.deep_link,
              destination: venue.city.name,
              stops: flightData.route ? flightData.route.length - 1 : 0,
              tripStrategy: flightData.tripStrategy,
              outboundDeparture: flightData.outboundDeparture,
              outboundArrival: flightData.outboundArrival,
              returnDeparture: flightData.returnDeparture,
              alternativeStrategy: flightData.alternativeStrategy,
              flyFrom: flightData.flyFrom,
              flyTo: flightData.flyTo,
            }
          }));
        } else {
           setFlightPrices(prev => ({ ...prev, [eventId]: null }));
        }
      } catch (flightError) {
        console.error(`Error fetching flight data for event ${eventId}:`, flightError);
         setFlightPrices(prev => ({ ...prev, [eventId]: null }));
      }
    }, []);

    // Reset flight state when the user changes their home city — previous prices
    // are for the wrong origin airport.
    useEffect(() => {
      setFlightPrices({});
      attemptedFlightFetchesRef.current.clear();
    }, [userLocation]);

    // Kick off a flight-price fetch for any event we haven't already attempted.
    // Uses a ref-tracked set instead of `flightPrices` so this effect doesn't
    // re-run after every fetch resolves.
    useEffect(() => {
      if (!userLocation || processedEvents.length === 0 || !team) return;

      const matchedUserCity = MAJOR_CITIES.find(city => city.fullName === userLocation);
      if (!matchedUserCity) return;

      processedEvents.forEach(event => {
        const venue = event._embedded?.venues?.[0];
        if (!needsFlight(userLocation, venue)) return;
        if (attemptedFlightFetchesRef.current.has(event.id)) return;

        attemptedFlightFetchesRef.current.add(event.id);
        fetchFlightPrice(event.id, userLocation, event.dates?.start?.dateTime, venue, team.sport);
      });
    }, [userLocation, processedEvents, needsFlight, fetchFlightPrice, team]);


  // Single backend call replaces the per-event price chain — backend handles
  // SeatGeek → TM details → TM calendar with TTL caching, so repeat visits
  // to the same team are instant.
  useEffect(() => {
    if (!team) return;
    let cancelled = false;

    const loadEvents = async () => {
      setIsLoading(true);
      setProcessedEvents([]);
      setFlightPrices({});
      attemptedFlightFetchesRef.current.clear();
      setError(null);

      try {
        const { events } = await fetchTeamEvents(team.name, team.sport);
        if (cancelled) return;
        setProcessedEvents(events || []);
        setIsLoading(false);
        setIsFetchingDetails(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading team events:', err);
        setError('Failed to load event data.');
        setIsLoading(false);
        setIsFetchingDetails(false);
      }
    };

    loadEvents();
    return () => { cancelled = true; };
  }, [team]);

  // --- Rendering Logic ---

  // Helper to determine home/away game
  const isHomeGame = (event, currentTeam) => {
    const venueCity = event._embedded?.venues?.[0]?.city?.name;
    return venueCity && currentTeam?.city && venueCity.toLowerCase() === currentTeam.city.toLowerCase();
  };

  if (!team) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="display text-3xl mb-3">Team not found.</p>
        <Link to="/" className="btn-ghost">← Back to search</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-5 md:py-8">
      {/* ───────── Page header (compact) ───────── */}
      <Link to="/" className="btn-ghost -ml-2 mb-2 text-sm">← All teams</Link>
      <div className="flex items-end justify-between flex-wrap gap-2 mb-5">
        <div>
          <p className="eyebrow mb-1">{team.sport} · Upcoming</p>
          <h1 className="display text-3xl md:text-5xl tracking-tightest leading-none">
            {team.name}
          </h1>
        </div>
        {!isLoading && processedEvents.length > 0 && (
          <p className="text-ink-3 text-sm">
            {processedEvents.length} {processedEvents.length === 1 ? 'game' : 'games'}
            {isFetchingDetails && <span className="ml-2 text-terracotta">· pricing…</span>}
          </p>
        )}
      </div>

      {/* ───────── City picker — only after pricing lands so it doesn't flash before games ───────── */}
      {!isLoading && !isFetchingDetails && processedEvents.length > 0 && (
        <div className="card p-3 md:p-3.5 mb-5 animate-rise relative z-20">
          <div className="flex items-center gap-3 flex-col sm:flex-row">
            <div className="hidden sm:block shrink-0 pl-1">
              <p className="eyebrow text-[0.65rem] leading-tight">Flying from</p>
              <p className="text-xs text-ink-3 leading-tight mt-0.5">Add your city for the all-in cost</p>
            </div>
            <div className="flex-1 w-full sm:w-auto sm:min-w-[260px]">
              <CitySearch
                value={userLocation}
                onChange={setUserLocation}
                placeholder="Your city — e.g. Montreal, QC"
              />
            </div>
          </div>
        </div>
      )}

      {/* ───────── Loading skeletons ───────── */}
      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map(i => (
            <div key={i} className="skeleton h-32" />
          ))}
        </div>
      )}

      {/* ───────── Error + empty states ───────── */}
      {error && processedEvents.length === 0 && !isFetchingDetails && (
        <div className="card p-12 text-center">
          <p className="display text-2xl mb-3">{error}</p>
          <p className="text-ink-3 mb-6">Something went wrong on our side. Try again or pick another team.</p>
          <Link to="/" className="btn-secondary">← Back to teams</Link>
        </div>
      )}

      {!isLoading && !isFetchingDetails && !error && processedEvents.length === 0 && (
        <div className="card p-12 text-center">
          <p className="display text-2xl mb-3">No upcoming games.</p>
          <p className="text-ink-3 mb-6">Nothing currently on the schedule for this team. Check back soon — schedules update often.</p>
          <Link to="/" className="btn-secondary">← Back to teams</Link>
        </div>
      )}

      {/* ───────── Event cards ───────── */}
      <div className="space-y-3">
      {!isLoading && processedEvents.map((event, idx) => {
          const displayPrice = event.ticketPrice;
          const priceMax = event.ticketPriceMax;
          const priceSource = event.priceSource;
          const venue = event._embedded?.venues?.[0];
          const flightInfo = flightPrices[event.id];
          const requiresFlight = needsFlight(userLocation, venue);
          const flightPrice = flightInfo?.price ?? 0;
          const showDetails = showFlightDetailsMap[event.id] || false;
          const hasAnyPrice = typeof displayPrice === 'number';
          const totalFlightCost = hasAnyPrice ? displayPrice + flightPrice : 0;
          const isHome = isHomeGame(event, team);

          return (
            <article
              key={event.id}
              className="card overflow-hidden reveal"
              style={{ animationDelay: `${Math.min(idx * 40, 280)}ms` }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-0">

                {/* ── LEFT: Event info (compact) ── */}
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <span className={`badge ${isHome ? 'badge-home' : 'badge-away'}`}>
                      <span className="badge-dot" />
                      {isHome ? 'Home' : 'Away'}
                    </span>
                    {event.dates?.start?.dateTime && (
                      <span className="text-xs text-ink-3 tracking-wide">
                        {formatDate(event.dates.start.dateTime)}
                      </span>
                    )}
                  </div>

                  <h3 className="display text-lg md:text-xl leading-snug mb-2 text-ink">
                    {event.name}
                  </h3>

                  <p className="text-sm text-ink-2 leading-snug">
                    {venue?.name && <span className="font-medium text-ink">{venue.name}</span>}
                    {venue?.name && venue?.city?.name && <span className="text-ink-4 mx-1.5">·</span>}
                    {venue?.city?.name && (
                      <span className="text-ink-3">
                        {venue.city.name}{venue?.state?.stateCode ? `, ${venue.state.stateCode}` : ''}
                      </span>
                    )}
                  </p>
                </div>

                {/* ── RIGHT: Pricing rail (compact) ── */}
                <div className={`lg:w-[300px] border-t lg:border-t-0 lg:border-l border-line ${
                  hasAnyPrice && flightInfo && requiresFlight && userLocation
                    ? 'bg-ocean text-paper'
                    : 'bg-vellum/40'
                } transition-colors`}>
                  <div className="p-4 md:p-5 h-full flex lg:flex-col items-center lg:items-stretch justify-between gap-3">

                    {/* Ticket price block */}
                    <div className="flex-1">
                      {hasAnyPrice ? (
                        userLocation && requiresFlight && flightInfo ? (
                          // All-in mode: prominent total + small breakdown
                          <>
                            <div className="flex items-baseline justify-between gap-3">
                              <p className="eyebrow text-paper/70 text-[0.65rem]">All-in</p>
                              <button
                                type="button"
                                aria-expanded={showDetails}
                                aria-controls={`flight-details-${event.id}`}
                                onClick={() => setShowFlightDetailsMap(prev => ({ ...prev, [event.id]: !prev[event.id] }))}
                                className="text-[11px] text-paper/70 hover:text-paper inline-flex items-center gap-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-paper/60 rounded px-1"
                              >
                                <span>{showDetails ? 'Hide' : 'Details'}</span>
                                <svg
                                  className={`h-3 w-3 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                            <div className="num text-4xl md:text-[2.6rem] leading-none mt-1">
                              ${Math.round(totalFlightCost)}
                            </div>
                            <p className="text-[11px] text-paper/60 mt-1">
                              ${Math.round(displayPrice)} ticket + ${Math.round(flightPrice)} flight
                            </p>
                          </>
                        ) : (
                          // Ticket-only mode
                          <>
                            <p className="eyebrow text-[0.65rem] mb-1">
                              From {priceSource ? `· ${priceSource}` : ''}
                            </p>
                            <div className="num text-3xl md:text-4xl leading-none">
                              ${Math.round(displayPrice)}
                              <span className="text-sm text-ink-3 ml-1 font-sans">/ticket</span>
                            </div>
                            {typeof priceMax === 'number' && priceMax > displayPrice && (
                              <p className="text-[11px] text-ink-3 mt-1">
                                up to ${Math.round(priceMax)} premium
                              </p>
                            )}
                          </>
                        )
                      ) : (
                        <>
                          <p className="eyebrow text-[0.65rem] mb-1">Tickets</p>
                          <p className="text-sm text-ink-2 leading-snug">
                            Live pricing isn't published.
                          </p>
                        </>
                      )}
                      {userLocation && requiresFlight && flightInfo === null && hasAnyPrice && (
                        <p className="text-[11px] text-ink-3 mt-2">
                          Couldn't price a flight for this route.
                        </p>
                      )}
                    </div>

                    {/* CTAs — horizontal on mobile, stacked on desktop */}
                    <div className="flex lg:flex-col gap-2 shrink-0 lg:w-full">
                      {event.url && (
                        <a
                          href={event.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary lg:w-full text-sm py-2.5"
                        >
                          {hasAnyPrice ? 'Tickets' : 'Check'}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7M7 7h10v10"/></svg>
                        </a>
                      )}
                      {(() => {
                        if (!(userLocation && requiresFlight && flightInfo && hasAnyPrice)) return null;
                        const expediaUrl = buildExpediaFlightUrl(
                          flightInfo.flyFrom,
                          flightInfo.flyTo,
                          flightInfo.outboundDeparture,
                          flightInfo.returnDeparture,
                        );
                        const bookUrl = expediaUrl || flightInfo.deep_link;
                        if (!bookUrl) return null;
                        return (
                          <a
                            href={bookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary lg:w-full text-sm py-2.5 bg-paper/10 border-paper/30 text-paper hover:bg-paper/20"
                          >
                            Flight
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7M7 7h10v10"/></svg>
                          </a>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expandable flight details — full-width drawer below the card body */}
              {userLocation && requiresFlight && flightInfo && hasAnyPrice && showDetails && (
                <div id={`flight-details-${event.id}`} className="border-t border-line bg-vellum/30 px-4 md:px-5 py-3.5 animate-fade">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3 text-sm">
                    <FlightStat label="Flight" value={`$${flightPrice.toFixed(0)}`} />
                    <FlightStat label="Airlines" value={flightInfo.airlines?.join(', ') ?? '—'} />
                    <FlightStat
                      label="Duration"
                      value={flightInfo.duration?.total
                        ? `${Math.floor(flightInfo.duration.total / 3600)}h ${Math.floor((flightInfo.duration.total % 3600) / 60)}m`
                        : '—'}
                    />
                    <FlightStat
                      label="Stops"
                      value={typeof flightInfo.stops === 'number'
                        ? (flightInfo.stops === 0 ? 'Nonstop' : `${flightInfo.stops} stop${flightInfo.stops > 1 ? 's' : ''}`)
                        : '—'}
                    />
                    {flightInfo.outboundArrival && (
                      <FlightStat label="Arrives" value={formatTripTime(flightInfo.outboundArrival)} />
                    )}
                    {flightInfo.returnDeparture && (
                      <FlightStat label="Returns" value={formatTripTime(flightInfo.returnDeparture)} />
                    )}
                    {flightInfo.tripStrategy && (
                      <div className="col-span-2 md:col-span-2">
                        <p className="eyebrow text-[0.65rem] mb-1">Trip plan</p>
                        <p className="text-ink leading-snug">
                          {flightInfo.tripStrategy === 'next-day-return'
                            ? 'Fly in day-of, return next day'
                            : flightInfo.tripStrategy === 'same-evening-return'
                            ? 'Fly in & out same day (red-eye)'
                            : flightInfo.tripStrategy === 'day-before-arrival'
                            ? 'Fly in day-before, return same day'
                            : '—'}
                        </p>
                        {flightInfo.alternativeStrategy && (
                          <p className="text-xs text-ink-3 mt-1">
                            vs ${flightInfo.alternativeStrategy.price.toFixed(0)} —{' '}
                            {flightInfo.alternativeStrategy.strategy === 'next-day-return' ? 'next-day return' :
                             flightInfo.alternativeStrategy.strategy === 'same-evening-return' ? 'same-night red-eye' :
                             flightInfo.alternativeStrategy.strategy}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};

// Tiny helper for the flight-details drawer — keeps the JSX above readable.
const FlightStat = ({ label, value }) => (
  <div>
    <p className="eyebrow text-[0.65rem] mb-1">{label}</p>
    <p className="text-ink leading-snug">{value}</p>
  </div>
);

export default TicketList;
